import moment from 'moment';
import { ModelBase } from '../../abstract/model-base.model';
import { Account } from '../../accounting/account.model';
import { Bank } from '../config/bank.model';
import { ClientContact } from '../../client/contact.model';
import { Currency } from '../../core/currency.model';
import { Journal } from '../../accounting/journal.model';
import { OpenBankingStatus } from './open-banking-status.model';
import { Partner } from '../../partner/partner.model';
import { BankStatementAutomation } from '../config/bank-statement-automation.model';
import { BankAccountType } from '../config/bank-account-type.model';
import { InsertableBankDetails } from './insertable-bank-details.model';
import { FkField, OmitField } from '../../../decorators/api-field.decorator';

export class BankAccount extends ModelBase {

    static override CLASS_NAME: string = 'BankAccount';

    private _account!: Account;
    private _accountNumber!: string;
    private _bank!: Bank;
    private _balance!: number;
    private _comingBalance!: number;
    private _contact?: ClientContact;
    private _currency!: Currency;
    private _dashboard!: boolean;
    private _domiciliation!: string | null;
    private _hasDuplicateTransactions!: boolean;
    private _holder!: string | null;
    private _iban!: string;
    private _initialBalance!: number;
    private _initialBalanceDate!: moment.Moment;
    private _insertableBankDetails!: InsertableBankDetails | null;
    private _journal!: Journal;
    private _journalCode!: string;
    private _label!: string;
    private _partner!: Partner;
    private _statementAutomation!: BankStatementAutomation;
    private _type!: BankAccountType | null;
    private _unjustifiedLinesCount!: number;

    // Agrégation bancaire - Open Banking
    private _openBankingStatus!: OpenBankingStatus;

    /**
     * Récupère l'initiale du nom commercial de la banque
     * @returns L'initiale ou '?' si aucun nom n'existe
     */
    public getBankInitial(): string {
        if (!this.bank?.commercialName) {
            return '?';
        }

        return this.bank.commercialName.charAt(0).toUpperCase();
    }

    /**
     * Récupère le logo de la banque ou retourne null si aucun logo n'existe
     * @returns Le chemin vers le logo ou null
     */
    public getBankLogo(): string | null {
        if (!this.bank) {
            return null;
        }
        return this.bank.getLogo();
    }

    public override toString(): string {
        let label = '';
        if (!!this.bank) {
            label = `${this.bank.commercialName} - ${this.label}`;
        } else {
            label = `${this.label}`;
        }

        if (this.iban && this.iban.length > 0) {
            label += ` - ${this.iban}`;
        }

        return label;
    }

    public get account(): Account {
        return this._account;
    }

    public set account(value: any) {
        this._setValue('_account', value, 'Account');
    }

    public get accountNumber(): string {
        return this._accountNumber;
    }

    public set accountNumber(value: any) {
        this._setValue('_accountNumber', value, 'string');
    }

    public get bank(): Bank {
        return this._bank;
    }

    public set bank(value: any) {
        this._setValue('_bank', value, 'Bank');
    }

    public get balance(): number {
        return this._balance;
    }

    public set balance(value: any) {
        this._setValue('_balance', value, 'float');
    }

    public get comingBalance(): number {
        return this._comingBalance;
    }

    public set comingBalance(value: any) {
        this._setValue('_comingBalance', value, 'float');
    }

    public get contact(): ClientContact | undefined {
        return this._contact;
    }

    public set contact(value: any) {
        this._setValue('_contact', value, 'ClientContact');
    }

    public get currency(): Currency {
        return this._currency;
    }

    public set currency(value: any) {
        this._setValue('_currency', value, 'Currency');
    }

    public get dashboard(): boolean {
        return this._dashboard;
    }

    public set dashboard(value: any) {
        this._setValue('_dashboard', value, 'boolean');
    }

    /**
     * Domiciliation du compte (banque, agence) — facultative, sans valeur par défaut (#964)
     */
    public get domiciliation(): string | null {
        return this._domiciliation;
    }

    public set domiciliation(value: any) {
        this._setValue('_domiciliation', value, 'string');
    }

    public get hasDuplicateTransactions(): boolean {
        return this._hasDuplicateTransactions;
    }

    public set hasDuplicateTransactions(value: any) {
        this._setValue('_hasDuplicateTransactions', value, 'boolean');
    }

    /**
     * Titulaire du compte — facultatif ; vide, le back retient la raison sociale du
     * dossier dans les mentions insérables (#964)
     */
    public get holder(): string | null {
        return this._holder;
    }

    public set holder(value: any) {
        this._setValue('_holder', value, 'string');
    }

    public get iban(): string {
        return this._iban;
    }

    public set iban(value: any) {
        this._setValue('_iban', value, 'string');
    }

    public get initialBalance(): number {
        return this._initialBalance;
    }

    public set initialBalance(value: any) {
        this._setValue('_initialBalance', value, 'float');
    }

    public get initialBalanceDate(): moment.Moment {
        return this._initialBalanceDate;
    }

    public set initialBalanceDate(value: any) {
        this._setValue('_initialBalanceDate', value, 'date');
    }

    /**
     * Mentions mises en forme par le back pour la liste « Insérer… » des coordonnées
     * bancaires (#964). Lecture seule, projetable par `$select` ; null sans IBAN.
     */
    @OmitField()
    public get insertableBankDetails(): InsertableBankDetails | null {
        return this._insertableBankDetails;
    }

    public set insertableBankDetails(value: any) {
        this._setValue('_insertableBankDetails', value, 'InsertableBankDetails');
    }

    public get journal(): Journal {
        return this._journal;
    }

    public set journal(value: any) {
        this._setValue('_journal', value, 'Journal');
    }

    public get journalCode(): string {
        return this._journalCode;
    }

    public set journalCode(value: any) {
        this._setValue('_journalCode', value, 'string');
    }

    public get label(): string {
        return this._label;
    }

    public set label(value: any) {
        this._setValue('_label', value, 'string');
    }

    public get openBankingStatus(): OpenBankingStatus {
        return this._openBankingStatus;
    }

    public set openBankingStatus(value: any) {
        this._setValue('_openBankingStatus', value, 'OpenBankingStatus');
    }

    public get partner(): Partner {
        return this._partner;
    }

    public set partner(value: any) {
        this._setValue('_partner', value, 'Partner');
    }

    public get statementAutomation(): BankStatementAutomation {
        return this._statementAutomation;
    }

    public set statementAutomation(value: any) {
        this._setValue('_statementAutomation', value, 'BankStatementAutomation');
    }

    /**
     * Nature du compte telle que la banque l'a vendu — null si le compte n'est pas qualifié
     */
    @FkField('code')
    public get type(): BankAccountType | null {
        return this._type;
    }

    public set type(value: any) {
        this._setValue('_type', value, 'BankAccountType');
    }

    public get unjustifiedLinesCount(): number {
        return this._unjustifiedLinesCount;
    }

    public set unjustifiedLinesCount(value: any) {
        this._setValue('_unjustifiedLinesCount', value, 'integer');
    }

    /*
     * Accesseurs raccourcis pour l'état Open Banking
     */

    /**
     * Solde communiqué par l'agrégateur bancaire, null si le compte n'est pas synchronisé
     */
    public get aggregatorBalance(): number | null {
        return this._openBankingStatus?.aggregatorBalance ?? null;
    }

    /**
     * Écart entre le solde de l'agrégateur bancaire et le solde calculé par Biifor,
     * null si le compte n'est pas synchronisé
     */
    public get balanceDiscrepancy(): number | null {
        if (this.aggregatorBalance === null) {
            return null;
        }
        return this.aggregatorBalance - (this.balance ?? 0);
    }

    /**
     * Indique si le solde de l'agrégateur bancaire diffère du solde calculé par Biifor
     */
    public get hasBalanceDiscrepancy(): boolean {
        return this.balanceDiscrepancy !== null && this.balanceDiscrepancy !== 0;
    }

    public get openBanking(): boolean {
        return this._openBankingStatus?.isConnected() ?? false;
    }

    public get openBankingErrorDisplay(): string {
        if (this._openBankingStatus?.message) {
            return this._openBankingStatus.message;
        }
        return 'Une erreur est survenue lors de la synchronisation de votre compte bancaire. Si le problème persiste, veuillez contacter le support.';
    }

    public get openBankingHasIssue(): boolean {
        return this._openBankingStatus?.hasIssue() ?? false;
    }

    public get openBankingLabel(): string {
        return this._openBankingStatus?.label ?? '';
    }

    public get openBankingLastUpdate(): moment.Moment | null {
        if (this._openBankingStatus?.lastSync) {
            return moment(this._openBankingStatus.lastSync);
        }
        return null;
    }

    public get openBankingMessage(): string {
        return !this.openBanking && !!this.bank.supportsOpenBanking ?
        "Ce compte n'est pas synchronisé automatiquement. Vous devez manuellement gérer l'import de vos transactions bancaires via le relevé PDF ou des fichiers CSV." :
        !this.bank.supportsOpenBanking ?
        `Malheureusement la banque ${this.bank.commercialName}
                    ne supporte pas la
                    synchronisation automatique via Open Banking. Nous vous informerons dès que cette fonctionnalité
                    sera disponible.`
        : this._openBankingStatus?.message ?? '';
    }

    public get openBankingNextTry(): moment.Moment | null {
        if (this._openBankingStatus?.nextSync) {
            return moment(this._openBankingStatus.nextSync);
        }
        return null;
    }

    public get openBankingState(): string {
        return this._openBankingStatus?.state ?? '';
    }

    public get openBankingSyncing(): boolean {
        return this._openBankingStatus?.syncing ?? false;
    }

    public get synchronizationIcon(): string {
        if (this.openBankingSyncing) {
            return 'pi pi-spin pi-sync';
        } else if (!this.openBanking) {
            return 'fa-solid fa-file-csv';
        } else if (this.openBankingHasIssue) {
            return 'fa-solid fa-circle-xmark';
        } else {
            return 'fa-solid fa-circle-check';
        }
    }

    public get synchronizationStatusSeverity(): string | undefined {
        if (this.openBankingSyncing) {
            return 'warn';
        } else if (this.openBankingHasIssue) {
            return 'error';
        } else if (!this.openBanking) {
            return 'info';
        } else {
            return 'success';
        }
    }
}
